// Master-P3 Wave 1.2 — BYO-LLM tab.
//
// UX rules (non-negotiable):
//   - Secrets are never displayed in plaintext after save. Only
//     `key_hint` = "********XXXX" (last 4 chars) is rendered.
//   - Declared capabilities and verified capabilities are rendered
//     separately, never merged.
//   - The "Fähigkeiten prüfen" (probe) button is disabled unless a
//     budget frame is active for bucket `text_llm`.
//   - test-connection is labelled "kostenlos" — it only performs an
//     SSRF check and key-format validation, no HTTP call to the
//     provider (verified in `TenantLLMAdapter.test_connection`).
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle }
  from "../../components/ui/alert";
import {
  KeyRound, Plug, ShieldCheck, ShieldAlert, Info, PlugZap,
  AlertTriangle,
} from "lucide-react";

const KIND_OPTIONS = [
  { value: "openai_compatible", label: "OpenAI-kompatibel",
    default_url: "https://api.openai.com/v1" },
  { value: "anthropic", label: "Anthropic",
    default_url: "https://api.anthropic.com" },
  { value: "emergent", label: "Emergent (Platform-Default)",
    default_url: "" },
  { value: "openai_compatible_self",
    label: "Selbst gehostet (OpenAI-kompatibel)",
    default_url: "" },
];

const CAPABILITIES = [
  { key: "text_generation", label: "Text-Generierung" },
  { key: "structured_output", label: "Strukturierte Ausgabe" },
  { key: "tool_use", label: "Tools/Function-Calls" },
  { key: "vision", label: "Bild-Verständnis" },
];

const CONNECTION_ERROR_HELP = {
  key_too_short: {
    title: "API-Key ungültig",
    hint: "Prüfe im Anbieter-Dashboard ob der Key aktiv ist.",
  },
  key_decrypt_failed: {
    title: "API-Key konnte nicht gelesen werden",
    hint: "Speichere den Key erneut. Mögliche Rotation im Backend.",
  },
  ssrf_dns_failure: {
    title: "DNS-Auflösung fehlgeschlagen",
    hint: "URL prüfen, keine private IP eingeben.",
  },
  ssrf_private_ip: {
    title: "Private IP blockiert",
    hint: ("Diese Adresse ist im Betreiber-Bereich nicht freigegeben."
           + " Bitte kontaktiere den Betreiber."),
  },
  ssrf_scheme_forbidden: {
    title: "Ungültiges Protokoll",
    hint: "Nur https:// ist erlaubt.",
  },
};

function humanizeConnectionReason(reason) {
  if (!reason) return null;
  // Reason comes as either "ssrf:ssrf_private_ip" (from
  // ConnectionProbe.reason built as `ssrf:{verdict.reason}`) or a bare
  // token like "key_too_short". Strip an optional "ssrf:" prefix and
  // map to a user-facing hint. The raw ssrf-* tokens themselves also
  // land in the mapping so both shapes route to the same message.
  const bare = reason.replace(/^ssrf:/, "");
  const hit = CONNECTION_ERROR_HELP[bare];
  if (hit) return hit;
  // Generic fallback preserves the raw reason for ops.
  return {
    title: "Verbindung fehlgeschlagen",
    hint: `Anbieter unterstützt möglicherweise nicht das erwartete `
        + `Format. Details: ${reason}`,
  };
}

function StatusChip({ status, hasKey, lastProbeAt }) {
  let label = "Nicht getestet";
  let tone = "bg-muted text-muted-foreground";
  let testId = "provider-status-untested";
  if (status === "active" && lastProbeAt) {
    label = "Verbunden";
    tone = "bg-emerald-100 text-emerald-800";
    testId = "provider-status-connected";
  } else if (status === "disabled") {
    label = "Deaktiviert";
    tone = "bg-red-100 text-red-800";
    testId = "provider-status-disabled";
  } else if (!hasKey) {
    label = "Kein Key";
    tone = "bg-amber-100 text-amber-800";
    testId = "provider-status-no-key";
  }
  return (
    <Badge data-testid={testId}
            className={`${tone} border-0 font-medium`}>{label}</Badge>
  );
}

function CapabilityChip({ label, state, testIdBase }) {
  // state ∈ {declared, verified, mismatch_declared_not_verified,
  //           mismatch_verified_not_declared, off}
  const map = {
    verified: { tone: "bg-emerald-100 text-emerald-800",
                suffix: "verified" },
    declared: { tone: "bg-sky-100 text-sky-800",
                suffix: "declared" },
    mismatch_declared_not_verified: {
      tone: "bg-red-100 text-red-800", suffix: "declared-only" },
    mismatch_verified_not_declared: {
      tone: "bg-amber-100 text-amber-800", suffix: "verified-only" },
    off: { tone: "bg-muted text-muted-foreground", suffix: "off" },
  };
  const cfg = map[state] || map.off;
  return (
    <Badge data-testid={`${testIdBase}-${cfg.suffix}`}
            className={`${cfg.tone} border-0`}>
      {label}
    </Badge>
  );
}

function ProviderWizard({ open, onOpenChange, onCreated }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    kind: "openai_compatible", label: "", base_url: "",
    api_key: "", default_model: "",
    rate_limit_per_minute: 30,
    caps: { text_generation: true, structured_output: false,
            tool_use: false, vision: false },
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setStep(1); setError(null); setSubmitting(false);
      setForm({
        kind: "openai_compatible", label: "", base_url: "",
        api_key: "", default_model: "",
        rate_limit_per_minute: 30,
        caps: { text_generation: true, structured_output: false,
                tool_use: false, vision: false },
      });
    }
  }, [open]);

  const setKind = (k) => {
    const opt = KIND_OPTIONS.find((o) => o.value === k);
    setForm((f) => ({ ...f, kind: k, base_url: opt?.default_url || "" }));
  };

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      // Backend accepts only the base 3 kinds; map self-hosted to
      // openai_compatible with an empty pre-fill.
      const backendKind = form.kind === "openai_compatible_self"
        ? "openai_compatible" : form.kind;
      const r = await api.post("/llm-providers", {
        kind: backendKind, label: form.label,
        base_url: form.base_url, api_key: form.api_key,
        default_model: form.default_model,
        rate_limit_per_minute: form.rate_limit_per_minute,
      });
      toast.success("Anbieter gespeichert");
      onCreated && onCreated(r.data);
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail || {};
      setError(detail.error || "unknown_error");
    } finally {
      setSubmitting(false);
    }
  };

  const needsKey = form.kind !== "emergent";
  const canSubmit = form.label
        && (!needsKey || (form.base_url && form.api_key.length >= 8))
        && form.default_model;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="provider-wizard"
                        className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Neuen KI-Anbieter verbinden</DialogTitle>
          <DialogDescription>
            Schritt {step} von 3. Der API-Key wird ausschließlich
            AES-verschlüsselt gespeichert und nach dem Speichern nie
            wieder im Klartext angezeigt.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4"
                data-testid="wizard-step-1">
            <div>
              <Label>Anbietertyp</Label>
              <Select value={form.kind} onValueChange={setKind}>
                <SelectTrigger data-testid="wizard-kind-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KIND_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}
                                   data-testid={`wizard-kind-${o.value}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Anbietername</Label>
              <Input value={form.label}
                       placeholder="z.B. Redaktions-OpenAI (prod)"
                       data-testid="wizard-label-input"
                       onChange={(e) => setForm((f) => ({ ...f,
                              label: e.target.value }))} />
            </div>
            {form.kind === "openai_compatible_self" && (
              <Alert data-testid="wizard-selfhosted-hint">
                <Info className="h-4 w-4" />
                <AlertTitle>Selbst-gehostete Adressen</AlertTitle>
                <AlertDescription className="text-xs">
                  Interne IPs und private Netze werden vom SSRF-Guard
                  abgelehnt. Bitte trage nur öffentlich erreichbare
                  HTTPS-URLs ein.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {step === 2 && needsKey && (
          <div className="space-y-4" data-testid="wizard-step-2">
            <div>
              <Label>API-Adresse (Base URL)</Label>
              <Input value={form.base_url}
                       placeholder="https://..."
                       data-testid="wizard-base-url-input"
                       onChange={(e) => setForm((f) => ({ ...f,
                              base_url: e.target.value }))} />
            </div>
            <div>
              <Label>API-Key</Label>
              <Input type="password" value={form.api_key}
                       autoComplete="off"
                       placeholder="Wird nur einmal beim Anlegen benötigt"
                       data-testid="wizard-api-key-input"
                       onChange={(e) => setForm((f) => ({ ...f,
                              api_key: e.target.value }))} />
              <p className="text-xs text-muted-foreground mt-1">
                Nach dem Speichern wird der Key nie wieder angezeigt —
                nur die letzten 4 Zeichen als Erkennungshilfe.
              </p>
            </div>
          </div>
        )}

        {step === 2 && !needsKey && (
          <div className="space-y-4" data-testid="wizard-step-2">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>Emergent Universal (Platform-Default)</AlertTitle>
              <AlertDescription>
                Kein API-Key und keine URL erforderlich — die Plattform
                stellt den Universal-Key bereit. Der Anbieter ist
                bereits ohne weitere Konfiguration nutzbar.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="wizard-step-3">
            <div>
              <Label>Modell-ID</Label>
              <Input value={form.default_model}
                       placeholder="z.B. gpt-4o-mini"
                       data-testid="wizard-model-input"
                       onChange={(e) => setForm((f) => ({ ...f,
                              default_model: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Deklarierte Fähigkeiten</Label>
              <p className="text-xs text-muted-foreground">
                Kreuze an, was dieser Anbieter kann. Wir prüfen später,
                ob das Modell die deklarierten Fähigkeiten tatsächlich
                erfüllt (Probe).
              </p>
              {CAPABILITIES.map((c) => (
                <label key={c.key}
                        className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={!!form.caps[c.key]}
                    data-testid={`wizard-cap-${c.key}`}
                    onCheckedChange={(v) => setForm((f) => ({
                      ...f, caps: { ...f.caps, [c.key]: !!v } }))} />
                  {c.label}
                </label>
              ))}
            </div>
            <div>
              <Label>Rate-Limit (Aufrufe/min)</Label>
              <Input type="number" min={1} max={600}
                       value={form.rate_limit_per_minute}
                       data-testid="wizard-rate-limit-input"
                       onChange={(e) => setForm((f) => ({ ...f,
                              rate_limit_per_minute:
                                parseInt(e.target.value || "0", 10) }))} />
            </div>
          </div>
        )}

        {error && (
          <Alert variant="destructive" data-testid="wizard-error">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>Fehler beim Speichern</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button variant="outline"
                    disabled={step === 1 || submitting}
                    onClick={() => setStep((s) => s - 1)}
                    data-testid="wizard-back">Zurück</Button>
          {step < 3 ? (
            <Button onClick={() => setStep((s) => s + 1)}
                     disabled={!form.label || (step === 2 && needsKey
                        && (!form.base_url || form.api_key.length < 8))}
                     data-testid="wizard-next">Weiter</Button>
          ) : (
            <Button onClick={submit}
                     disabled={!canSubmit || submitting}
                     data-testid="wizard-submit">
              Speichern
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProviderRow({ provider, budgetActive, onProbe, onTest,
                        onReloadOne }) {
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const declared = provider.capabilities || {};
  const verified = provider.last_probe_result || {};
  const capState = (k) => {
    const d = !!declared[k];
    const v = !!verified[k];
    if (!d && !v) return "off";
    if (d && v) return "verified";
    if (d && !v) return provider.last_probe_at
                    ? "mismatch_declared_not_verified" : "declared";
    return "mismatch_verified_not_declared";
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      const r = await api.post(
        `/llm-providers/${provider.id}/test-connection`);
      setTestResult(r.data);
      if (r.data.ok) toast.success("Verbindungstest erfolgreich");
      else toast.error("Verbindungstest fehlgeschlagen");
      onTest && onTest(provider.id, r.data);
    } finally { setBusy(false); }
  };

  const handleProbe = async () => {
    setBusy(true);
    try {
      const r = await api.post(
        `/llm-providers/${provider.id}/probe-capabilities`);
      toast.success("Fähigkeitsprüfung abgeschlossen");
      onProbe && onProbe(provider.id, r.data);
      onReloadOne && await onReloadOne(provider.id);
    } catch (e) {
      const detail = e.response?.data?.detail || {};
      if (e.response?.status === 402) {
        toast.error("Kein aktives Budget-Frame — Probe nicht möglich");
      } else {
        toast.error(`Probe fehlgeschlagen: ${detail.error || detail}`);
      }
    } finally { setBusy(false); }
  };

  const testHelp = testResult && !testResult.ok
    ? humanizeConnectionReason(testResult.reason) : null;

  return (
    <Card data-testid={`provider-row-${provider.id}`}>
      <CardHeader className="flex flex-row items-start justify-between
                                gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-3">
            <span data-testid={`provider-label-${provider.id}`}>
              {provider.label || "(ohne Label)"}
            </span>
            <StatusChip status={provider.status}
                          hasKey={provider.has_api_key}
                          lastProbeAt={provider.last_probe_at} />
          </CardTitle>
          <CardDescription className="mt-1 flex items-center gap-2">
            <span>{provider.kind}</span>
            <span>·</span>
            <span>{provider.default_model || "kein Modell gewählt"}</span>
            {provider.key_hint && (
              <>
                <span>·</span>
                <span className="font-mono text-xs"
                        data-testid={`provider-key-hint-${provider.id}`}>
                  {provider.key_hint}
                </span>
              </>
            )}
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handleTest}
                     disabled={busy}
                     data-testid={`btn-test-connection-${provider.id}`}>
            <Plug className="h-3 w-3 mr-1" />
            Verbindung testen (kostenlos)
          </Button>
          <Button size="sm" onClick={handleProbe}
                     disabled={busy || !budgetActive}
                     title={budgetActive ? "" :
                       "Aktives Budget-Frame erforderlich"}
                     data-testid={`btn-probe-${provider.id}`}>
            <PlugZap className="h-3 w-3 mr-1" />
            Fähigkeiten prüfen (kostenpflichtig)
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-1">
            Deklariert
          </div>
          <div className="flex flex-wrap gap-1"
                data-testid={`declared-capabilities-${provider.id}`}>
            {CAPABILITIES.map((c) => (
              <CapabilityChip key={c.key} label={c.label}
                              testIdBase={`declared-${c.key}-${provider.id}`}
                              state={declared[c.key] ? "declared" : "off"} />
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-1">
            Verifiziert (aus Probe)
          </div>
          <div className="flex flex-wrap gap-1"
                data-testid={`verified-capabilities-${provider.id}`}>
            {CAPABILITIES.map((c) => (
              <CapabilityChip key={c.key} label={c.label}
                              testIdBase={`verified-${c.key}-${provider.id}`}
                              state={capState(c.key)} />
            ))}
          </div>
          {!provider.last_probe_at && (
            <p className="text-xs text-muted-foreground mt-1"
                data-testid={`no-probe-yet-${provider.id}`}>
              Noch keine Probe durchgeführt.
            </p>
          )}
        </div>
        {testHelp && (
          <Alert variant="destructive"
                  data-testid={`test-connection-help-${provider.id}`}>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{testHelp.title}</AlertTitle>
            <AlertDescription>{testHelp.hint}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default function LLMProvidersTab() {
  const [providers, setProviders] = useState([]);
  const [budget, setBudget] = useState({ active: false });
  const [wizardOpen, setWizardOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, b] = await Promise.all([
        api.get("/llm-providers"),
        api.get("/llm-providers/_budget-status"),
      ]);
      setProviders(p.data.items || []);
      setBudget(b.data || { active: false });
    } catch (e) {
      toast.error("Konnte KI-Anbieter nicht laden");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const reloadOne = async (pid) => {
    try {
      const r = await api.get(`/llm-providers/${pid}`);
      setProviders((prev) => prev.map(
        (p) => (p.id === pid ? r.data : p)));
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="space-y-6" data-testid="llm-providers-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">KI-Anbieter</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Verbinde eigene LLM-Anbieter (BYO-Key). Der API-Key wird
            AES-verschlüsselt und nie im Klartext ausgeliefert.
          </p>
        </div>
        <Button onClick={() => setWizardOpen(true)}
                 data-testid="btn-open-wizard">
          <KeyRound className="h-4 w-4 mr-2" />
          Neuen Anbieter verbinden
        </Button>
      </div>

      {!budget.active && (
        <Alert data-testid="budget-frame-missing-alert">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Kein aktives Budget-Frame</AlertTitle>
          <AlertDescription>
            Die kostenpflichtige Fähigkeitsprüfung (Probe) ist
            deaktiviert bis ein Owner ein Budget-Frame für den Bucket
            <code className="mx-1">text_llm</code> aktiviert. Der
            kostenlose Verbindungstest bleibt verfügbar.
          </AlertDescription>
        </Alert>
      )}
      {budget.active && (
        <Alert data-testid="budget-frame-active-alert">
          <ShieldCheck className="h-4 w-4" />
          <AlertTitle>Budget-Frame aktiv</AlertTitle>
          <AlertDescription>
            Bucket <code>text_llm</code> —
            verbleibend {(budget.remaining_eur || 0).toFixed(2)} EUR von
            {" "}{(budget.limit_eur || 0).toFixed(2)} EUR.
          </AlertDescription>
        </Alert>
      )}

      {loading && (
        <p className="text-sm text-muted-foreground"
            data-testid="providers-loading">
          Wird geladen...
        </p>
      )}
      {!loading && providers.length === 0 && (
        <Card data-testid="providers-empty">
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground">
              Noch keine Anbieter konfiguriert. Standard: Emergent
              Universal (Platform-Default) ist ohne Config nutzbar.
            </p>
          </CardContent>
        </Card>
      )}
      <div className="space-y-4"
            data-testid="providers-list">
        {providers.map((p) => (
          <ProviderRow key={p.id} provider={p}
                          budgetActive={budget.active}
                          onReloadOne={reloadOne} />
        ))}
      </div>

      <ProviderWizard open={wizardOpen}
                        onOpenChange={setWizardOpen}
                        onCreated={() => load()} />
    </div>
  );
}
